/* eslint-disable react-hooks/exhaustive-deps */
import { Checkbox, Grid, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';
import RefreshButton from 'components/RefreshButton';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import i18n from 'i18n';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import Permission from 'components/ValidPermission';
import { useCheckboxes } from 'hooks';
import { PermissionsData } from 'modules/authenticate/interfaces';
import {
  getLeakedCredentialsUsersRequest,
  resetPasswordLeakedCredentialsRequest,
} from 'modules/partners/actions';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import {
  LeakedCredentialsUsersFields,
  LeakedCredentialsUsersProps,
} from 'modules/partners/interfaces';
import { cyrb53Hash, formatDateWithMoment } from 'utils/helpers';
import { ModalFields } from 'modules/modal/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import Button from 'components/Button';
import PageTitle from 'components/PageTitle';
import styles from './styles';
import ListClients from './ListClients';

const useStyles = makeStyles(styles);

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'reset_leaked_credentials_user_password',
];

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

interface Props {
  isLoading: boolean;
  leakedCredentialsUsers: LeakedCredentialsUsersProps;
  permissions?: PermissionsData;
  getLeakedCredentialsUsersRequest: (props) => void;
  resetPasswordLeakedCredentialsRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
}

const LeakedCredentials = ({
  isLoading,
  leakedCredentialsUsers,
  permissions,
  getLeakedCredentialsUsersRequest,
  resetPasswordLeakedCredentialsRequest,
  modalClose,
  modalOpen,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const { checks, handleCheck, handleAllCheck, isChecked } = useCheckboxes<
    LeakedCredentialsUsersFields,
    number
  >({
    items: leakedCredentialsUsers?.results,
    isItemChecked: (item, check) => item?.id === check,
    createCheckItem: item => item?.id,
  });
  const isAllChecked =
    !!leakedCredentialsUsers?.results?.length &&
    leakedCredentialsUsers.results.every(item => isChecked(item?.id));

  const headers = [
    {
      key: 'name',
      i18n: 'USER_KEY',
      order: true,
    },
    {
      key: 'email',
      i18n: 'EMAIL_KEY',
      order: true,
    },
    {
      key: 'date',
      i18n: 'DATE_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'clients_count',
      i18n: 'CLIENTS_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'password_reset',
      i18n: 'PASSWORD_RESET_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'admin_notified',
      i18n: 'ADMIN_NOTIFIED_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: '',
      i18n: 'ACTIONS_KEY',
      centerTitle: true,
      hidden:
        !!permissions && !permissions.reset_leaked_credentials_user_password,
    },
  ].filter(item => !item.hidden);

  const getUsers = () => {
    getLeakedCredentialsUsersRequest({
      query,
    });
  };

  React.useEffect(() => {
    getUsers();
  }, [getLeakedCredentialsUsersRequest, query]);

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
        className={classes.titleMargin}
      >
        <PageTitle
          title={i18n.t('LEAKED_CREDENTIALS_KEY')}
          subtitle={i18n.t('LEAKED_CREDENTIALS_SUBTITLE_KEY')}
        />

        <Grid item>
          <RefreshButton
            id="btn-refresh"
            onClick={() => getUsers()}
            animate={isLoading}
            className={classes.refreshButton}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
      <Grid container justify="space-between" alignItems="center">
        <Grid item xs={6}>
          <SearchBar
            id="search-credentials"
            isLoading={isLoading}
            searchValue={searchValue}
            placeholder={i18n.t('SEARCH_KEY')}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
        </Grid>
        <Grid item>
          <Permission value={requiredActionsPermissions}>
            <Button
              outlineBlue
              startIcon={<RotateLeftIcon />}
              className={classes.resetButton}
              disabled={checks.length === 0}
              onClick={() =>
                modalOpen({
                  title: i18n.t('FORCE_RESET_PASSWORD_KEY'),
                  content: i18n.t('CONFIRM_MULTIPLE_RESET_PASSWORD_KEY'),
                  footer: (
                    <Grid>
                      <Button
                        submitButton
                        id="submit-button"
                        onClick={() => {
                          resetPasswordLeakedCredentialsRequest({
                            ids: checks,
                            query,
                          });
                          modalClose();
                        }}
                      >
                        {i18n.t('CONFIRM_KEY')}
                      </Button>
                      <Button
                        id="cancel-button"
                        variant="outlined"
                        onClick={modalClose}
                      >
                        {i18n.t('CANCEL_KEY')}
                      </Button>
                    </Grid>
                  ),
                  noBorder: true,
                  maxWidth: 'sm',
                })
              }
            >
              {i18n.t('FORCE_RESET_SELECTED_KEY')}
            </Button>
          </Permission>
        </Grid>
      </Grid>
      <TableItems
        header={
          <>
            <Permission value={requiredActionsPermissions}>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  checked={isAllChecked}
                  onClick={() => handleAllCheck(isAllChecked)}
                />
              </TableCell>
            </Permission>
            {headers.map(item => (
              <TableCell
                centerTitle={item?.centerTitle}
                key={item?.key}
                order={item?.order}
                markOderItem={itemOrder === item?.key}
                handleOrder={() =>
                  item?.order ? handleOrder(item?.key) : null
                }
              >
                {i18n.t(item?.i18n)}
              </TableCell>
            ))}
          </>
        }
        renderItems={(item: LeakedCredentialsUsersFields, index) => (
          <TableRow
            className={clsx(classes.summaryRowTable, {
              [classes.envRowDirect]:
                !!permissions && permissions.list_environment_instances,
            })}
            key={cyrb53Hash(index)}
          >
            <Permission value={requiredActionsPermissions}>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  onClick={() => handleCheck(item?.id)}
                  checked={isChecked(item?.id)}
                />
              </TableCell>
            </Permission>
            <TableCell copyable className={classes.textField}>
              {`${item?.name} ${item?.last_name}`}
            </TableCell>
            <TableCell copyable className={classes.textField}>
              {item?.email}
            </TableCell>
            <TableCell className={classes.textFieldCenter}>
              <Typography className={classes.textFieldCenterText}>
                {formatDateWithMoment(item?.date)}
              </Typography>
            </TableCell>
            <TableCell className={classes.textFieldCenter}>
              <div className={classes.clientsCell}>
                <span>{item?.clients_count}</span>
                <Permission value="list_leaked_credentials_clients">
                  <VisibilityOutlinedIcon
                    className={classes.eyeIcon}
                    onClick={() =>
                      modalOpen({
                        title: i18n.t('CLIENTS_KEY'),
                        content: <ListClients userUuid={item?.usr_uuid} />,
                        noBorder: true,
                        maxWidth: 'md',
                      })
                    }
                  />
                </Permission>
              </div>
            </TableCell>
            <TableCell className={classes.textFieldCenter}>
              <Typography className={classes.textFieldCenterText}>
                {item?.password_reset ? i18n.t('YES_KEY') : i18n.t('NO_KEY')}
              </Typography>
            </TableCell>
            <TableCell className={classes.textFieldCenter}>
              <Typography className={classes.textFieldCenterText}>
                {item?.admin_notified ? i18n.t('YES_KEY') : i18n.t('NO_KEY')}
              </Typography>
            </TableCell>

            <Permission value={requiredActionsPermissions}>
              <TableCell textAlignCenter>
                <MenuActions id={`btn-options-leaked-users-${index}`}>
                  <MenuActionItem
                    id="btn-reset-user-password"
                    onClick={() =>
                      modalOpen({
                        title: i18n.t('FORCE_RESET_PASSWORD_KEY'),
                        content: i18n.t('CONFIRM_RESET_PASSWORD_KEY'),
                        footer: (
                          <Grid>
                            <Button
                              submitButton
                              id="submit-button"
                              onClick={() => {
                                resetPasswordLeakedCredentialsRequest({
                                  ids: [item?.id],
                                  query,
                                });
                                modalClose();
                              }}
                            >
                              {i18n.t('CONFIRM_KEY')}
                            </Button>
                            <Button
                              id="cancel-button"
                              variant="outlined"
                              onClick={modalClose}
                            >
                              {i18n.t('CANCEL_KEY')}
                            </Button>
                          </Grid>
                        ),
                        noBorder: true,
                        maxWidth: 'sm',
                      })
                    }
                  >
                    <Typography className={classes.menuOptions}>
                      {i18n.t('FORCE_RESET_PASSWORD_KEY')}
                    </Typography>
                  </MenuActionItem>
                </MenuActions>
              </TableCell>
            </Permission>
          </TableRow>
        )}
        data={leakedCredentialsUsers}
        isLoading={isLoading}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        notFoundText={i18n.t('LOGS_PAGE.NO_LOGS_KEY')}
      />
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  partners: { leakedCredentialsUsers, isLoadingLeakedCredentialsUsers },
}: ApplicationState) => ({
  permissions: data?.permissions,
  leakedCredentialsUsers,
  isLoading: isLoadingLeakedCredentialsUsers,
});

export default connect(mapStateToProps, {
  getLeakedCredentialsUsersRequest,
  resetPasswordLeakedCredentialsRequest,
  modalClose,
  modalOpen,
})(LeakedCredentials);
